import { LogPhotoStrip } from "../LogPhotoStrip";
import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Circle, MessageSquare } from "lucide-react";
import { getAthleteActivityFeed } from "../../lib/api";

function statusMeta(s) {
  if (s === "complete") return { icon: CheckCircle2, color: "#2F855A", label: "Complete" };
  if (s === "partial") return { icon: AlertCircle, color: "#D69E2E", label: "Partial" };
  if (s === "skipped") return { icon: Circle, color: "#7A1F2B", label: "Missed" };
  return { icon: Circle, color: "#A8A099", label: s || "—" };
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) {
    const hours = Math.floor(ms / 3600000);
    if (hours < 1) return "Just now";
    return `${hours}h ago`;
  }
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function AthleteActivityFeed({ athleteId }) {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    if (!athleteId) return;
    getAthleteActivityFeed(athleteId)
      .then(setLogs)
      .catch(() => setLogs([]));
  }, [athleteId]);

  if (logs === null) {
    return <div className="text-xs text-[var(--tir-muted)] py-4">Loading activity…</div>;
  }
  if (logs.length === 0) {
    return (
      <div className="border border-dashed border-[var(--tir-border)] py-8 text-center text-xs text-[var(--tir-muted)]">
        No activity yet — invite the athlete to the portal so they can start logging workouts.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="athlete-activity-feed">
      {logs.slice(0, 20).map((log) => {
        const m = statusMeta(log.status);
        const Icon = m.icon;
        return (
          <div
            key={log.id}
            className="bg-white border border-[var(--tir-border)] p-3 flex gap-3"
            data-testid={`activity-item-${log.id}`}
          >
            <div style={{ color: m.color }} className="pt-0.5">
              <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-sm font-semibold text-[var(--tir-ink)] truncate">
                  {log.workout_title || (log.prescribed?.type) || "Workout"}
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)] whitespace-nowrap">
                  {fmtAgo(log.updated_at)}
                </span>
              </div>
              <div className="text-[11px] text-[var(--tir-muted)] mt-0.5">
                {log.program_title && <span className="text-[var(--tir-burgundy)] font-semibold">{log.program_title}</span>}
                {log.program_title && " · "}
                Week {log.week_number} · {log.day_label}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] font-mono-tir">
                <span style={{ color: m.color }} className="font-semibold uppercase tracking-wider">
                  {m.label}
                </span>
                {log.distance_actual_km != null && (
                  <span className="text-[var(--tir-ink)]">{log.distance_actual_km} km</span>
                )}
                {log.duration_actual_min != null && (
                  <span className="text-[var(--tir-ink)]">{log.duration_actual_min} min</span>
                )}
                {log.rpe_actual != null && (
                  <span className="text-[var(--tir-ink)]">RPE {log.rpe_actual}</span>
                )}
                {log.prescribed?.duration_or_distance && (
                  <span className="text-[var(--tir-muted)]">
                    (Rx: {log.prescribed.duration_or_distance})
                  </span>
                )}
              </div>
              {log.athlete_note && (
                <div className="mt-2 text-xs text-[var(--tir-ink)] flex gap-1.5 leading-snug">
                  <MessageSquare size={11} className="shrink-0 text-[var(--tir-muted)] mt-0.5" />
                  <span className="italic">"{log.athlete_note}"</span>
                </div>
              )}
              <div className="mt-1.5"><LogPhotoStrip log={log} size="sm" testid={`feed-photos-${log.id}`} /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
